/* eslint-disable react/require-default-props */
import React from "react";
import PropTypes from "prop-types";
import { useIntl } from "react-intl";
import {
  Button,
  FlexColumn,
  FlexRow,
  Typography,
  VideoPlayer,
} from "design-system";
import {
  ArrowRightIcon,
  CircleCheckIcon,
  LockIcon,
} from "design-system/components/Icons";
import GuidanceStepHeader from "../../components/GuidanceStepHeader";

export default function VideoStep({
  guidance,
  logoSrc,
  videoPlayer,
  onContinue,
  canContinue,
}) {
  const intl = useIntl();
  const { complete: videoComplete } = videoPlayer;
  const requireFullCompletion = guidance.require_full_completion ?? true;
  const showStatusNote = videoComplete || requireFullCompletion;

  const footer = (
    <FlexRow
      align="center"
      justify={showStatusNote ? "between" : "end"}
      fullWidth
      wrap
      gap="var(--ds-space-4)"
    >
      {videoComplete ? (
        <FlexRow align="center" fullWidth={false} gap="var(--ds-space-2)">
          <CircleCheckIcon
            size={18}
            className="ds-icon--color-success"
            aria-hidden
          />
          <Typography
            component="span"
            variant="bodySmall"
            fontWeight="semibold"
            className="ld-guidance-step__success"
          >
            {intl.formatMessage({ id: "ld-watched-in-full" })}
          </Typography>
        </FlexRow>
      ) : requireFullCompletion ? (
        <FlexRow align="center" fullWidth={false} gap="var(--ds-space-2)">
          <LockIcon size={16} className="ds-icon--color-muted" aria-hidden />
          <Typography component="span" variant="bodySmall" color="ui-subtle">
            {intl.formatMessage({ id: "ld-video-no-skip" })}
          </Typography>
        </FlexRow>
      ) : null}
      <Button
        variant="primary"
        size="base"
        className="ld-guidance-step__continue"
        icon={<ArrowRightIcon size={18} />}
        iconPosition="right"
        disabled={!canContinue}
        onClick={onContinue}
      >
        {intl.formatMessage({ id: "continue" })}
      </Button>
    </FlexRow>
  );

  return (
    <FlexColumn
      align="stretch"
      fullWidth
      gap="var(--ds-space-6)"
      className="ld-guidance-step"
    >
      <GuidanceStepHeader
        label={intl.formatMessage(
          { id: "ld-guidance-of" },
          {
            current: guidance.stepNumber,
            total: guidance.stepTotal,
          },
        )}
        title={guidance.title}
        instruction={guidance.instruction}
        labelVariant="bodySmall"
        titleVariant="headingXLarge"
        titleFontWeight="bold"
      />

      {/* `key` is required: without a remount the next step inherits the
          previous video's play, mute and maximized state. */}
      <VideoPlayer
        key={guidance.id}
        src={guidance.videoSrc}
        poster={guidance.posterSrc}
        preview={guidance.preview}
        previewVariant={guidance.previewVariant ?? "video"}
        logoSrc={guidance.logoSrc ?? logoSrc}
        logoAlt={guidance.logoAlt}
        ariaLabel={guidance.title}
        resetKey={guidance.id}
        currentTime={videoPlayer.currentTime}
        maxWatched={videoPlayer.maxWatched}
        playing={videoPlayer.playing}
        duration={videoPlayer.duration}
        complete={videoPlayer.complete}
        setPlaying={videoPlayer.setPlaying}
        onDurationChange={videoPlayer.syncDuration}
        onTimeUpdate={videoPlayer.syncProgress}
        onTogglePlayback={videoPlayer.togglePlayback}
        onSeek={videoPlayer.seek}
        onEnded={videoPlayer.handleEnded}
        formatTime={videoPlayer.formatTime}
        showControls={guidance.showControls ?? true}
        mutedNote={intl.formatMessage({ id: "ld-video-muted-counts" })}
        footer={footer}
      />
    </FlexColumn>
  );
}

VideoStep.propTypes = {
  guidance: PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    stepNumber: PropTypes.number.isRequired,
    stepTotal: PropTypes.number.isRequired,
    title: PropTypes.string.isRequired,
    instruction: PropTypes.string.isRequired,
    require_full_completion: PropTypes.bool,
    videoSrc: PropTypes.string.isRequired,
    posterSrc: PropTypes.string,
    logoSrc: PropTypes.string,
    logoAlt: PropTypes.string,
    preview: PropTypes.shape({
      tag: PropTypes.node,
      heading: PropTypes.node,
      body: PropTypes.node,
      segmentCount: PropTypes.number,
      activeSegment: PropTypes.number,
    }),
    previewVariant: PropTypes.oneOf(["video", "card"]),
    showControls: PropTypes.bool,
  }).isRequired,
  logoSrc: PropTypes.string,
  videoPlayer: PropTypes.shape({
    currentTime: PropTypes.number.isRequired,
    maxWatched: PropTypes.number.isRequired,
    playing: PropTypes.bool.isRequired,
    duration: PropTypes.number.isRequired,
    complete: PropTypes.bool.isRequired,
    setPlaying: PropTypes.func.isRequired,
    syncDuration: PropTypes.func.isRequired,
    syncProgress: PropTypes.func.isRequired,
    togglePlayback: PropTypes.func.isRequired,
    seek: PropTypes.func.isRequired,
    handleEnded: PropTypes.func.isRequired,
    formatTime: PropTypes.func.isRequired,
  }).isRequired,
  onContinue: PropTypes.func.isRequired,
  canContinue: PropTypes.bool.isRequired,
};
